import React, { useContext, useMemo, useRef, ReactNode, useCallback, createContext, useState } from 'react';
import { ICardEvent, ICardTrackEvent } from 'components/card/wl/cardForm/CardFormWL.types';
import CardFormAntiBot from './CardFormAntiBot';

export interface ICardFormMetrics {
  trackEvent: (event: ICardEvent) => void;
  resetTrackEvent: () => void;
  events?: ICardTrackEvent[];
  startTime: number;
  endTime: number;
}

interface CardFormMetricsProviderProps {
  children: ReactNode;
}

export const CardFormMetricsContext = createContext<ICardFormMetrics | undefined>(undefined);

export function CardFormMetricsProvider({ children }: CardFormMetricsProviderProps) {
  const antiBot = useRef(new CardFormAntiBot()).current;
  const [version, setVersion] = useState(0);

  const trackEvent = useCallback(
    (event: ICardEvent) => {
      antiBot.trackEvent(event);
      setVersion(version + 1);
    },
    [antiBot]
  );

  const resetTrackEvent = useCallback(() => {
    antiBot.reset();
    setVersion(v => v + 1);
  }, [antiBot]);

  const value = useMemo(
    () => ({
      trackEvent,
      resetTrackEvent,
      events: antiBot.getEvents(),
      startTime: antiBot.getStartTime(),
      endTime: antiBot.getEndTime(),
    }),
    [trackEvent, resetTrackEvent, version]
  );

  return <CardFormMetricsContext.Provider value={value}>{children}</CardFormMetricsContext.Provider>;
}

export const useCardFormMetrics = (): ICardFormMetrics => {
  const context = useContext(CardFormMetricsContext);
  if (context === undefined) {
    throw new Error('useCardFormMetrics must be used within a CardFormMetricsProvider');
  }
  return context;
};
